


import { Component, Vue, Watch } from "vue-property-decorator";

import { parsePhoneNumberFromString } from "libphonenumber-js";

import { Customers } from "App";
import { GroupUser } from "App/Contracts";
import { EventBus } from "App/IOC";
import { Column, PagerSorter } from "App/Models";

import { CustomValue } from "@Internal/Models";
import { Option } from "Web/Contracts";

import Table from "Web/Components/Table/Index.vue";
import SimpleSelect from "Web/Components/V2/Select/Simple.vue";

@Component({
    components: {
        SimpleSelect,
        Table
    }
})
export default class UsersView extends Vue {
    private users: GroupUser[] = [];
    private loading: boolean = true;
    private columns: Column[] = [];

    /* Filtres + pagination côté serveur. Statut "enabled" (actif) par défaut. Le vocabulaire suit
       l'endpoint /console/users : enabled / pending / disabled. */
    private statusFilter: string = "enabled";
    private keyword: string = "";
    private pagerSorter: PagerSorter = new PagerSorter( 10 );

    /* Debounce de la recherche pour ne pas requêter à chaque frappe. */
    private keywordTimer: number = 0;

    private created(): void {
        this.columns = [
            Column.Static( "Name", this.$t( "CUSTOMERS_userColumnName" ) as string ),
            Column.Static( "Email", this.$t( "CUSTOMERS_userColumnEmail" ) as string ),
            Column.Static( "Phone", this.$t( "CUSTOMERS_userColumnPhone" ) as string ),
            Column.Static( "Status", this.$t( "CUSTOMERS_userColumnStatus" ) as string ),
            Column.Static( "CreatedAt", this.$t( "CUSTOMERS_userColumnCreated" ) as string ),
            Column.Static(
                "LastActivity",
                this.$t( "CUSTOMERS_userColumnLastActivity" ) as string
            ),
            Column.Static( "Group", this.$t( "CUSTOMERS_userColumnGroup" ) as string ),
            Column.Static( "Company", this.$t( "CUSTOMERS_userColumnCompany" ) as string ),
            Column.Static( "Owner", this.$t( "CUSTOMERS_userColumnOwner" ) as string )
        ];

        this.fetch();
    }

    /* LA LISTE DU STATUT AU NOUVEAU DS ( Fred, 26/09/2026 ) : `SimpleSelect`, comme
       les ecrans deja refaits, a la place du `<select>` du navigateur. « Tous »
       porte l'identifiant `all` — un identifiant vide se confondrait avec
       « rien de choisi » — et part au service comme un filtre vide. */
    private get statusOptions(): Option[] {
        return [
            new CustomValue<string>( this.$t( "CUSTOMERS_userFilterAll" ) as string, "all" ),
            new CustomValue<string>( this.$t( "CUSTOMERS_userStatusActive" ) as string, "enabled" ),
            new CustomValue<string>(
                this.$t( "CUSTOMERS_userStatusProcessing" ) as string, "pending"
            ),
            new CustomValue<string>(
                this.$t( "CUSTOMERS_userStatusInactive" ) as string, "disabled"
            )
        ];
    }

    private get selectedStatus(): Option | undefined {
        const id = this.statusFilter === "" ? "all" : this.statusFilter;

        return this.statusOptions.find( ( option: Option ) => option.Id === id );
    }

    private selectStatus( option: Option ): void {
        this.statusFilter = option.Id === "all" ? "" : String( option.Id );
    }

    @Watch( "statusFilter" )
    private onStatusChange(): void {
        this.pagerSorter.Page = 1;
        this.fetch();
    }

    @Watch( "keyword" )
    private onKeywordChange(): void {
        window.clearTimeout( this.keywordTimer );

        this.keywordTimer = window.setTimeout( () => {
            this.pagerSorter.Page = 1;
            this.fetch();
        }, 300 );
    }

    private onPager( pagerSorter: PagerSorter ): void {
        this.pagerSorter = pagerSorter;
        this.fetch();
    }

    private async fetch(): Promise<void> {
        this.loading = true;

        const [ result, error ] = await Customers.ListUsers(
            this.pagerSorter.Page,
            this.pagerSorter.Limit,
            this.statusFilter,
            this.keyword.trim()
        );

        this.loading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.users = result.Users;
        this.pagerSorter.Total = result.Total;
    }

    private statusLabel( status: string ): string {
        switch( status ) {
            case "enabled":
                return this.$t( "CUSTOMERS_userStatusActive" ) as string;

            case "pending":
                return this.$t( "CUSTOMERS_userStatusProcessing" ) as string;

            case "disabled":
                return this.$t( "CUSTOMERS_userStatusInactive" ) as string;

            default:
                return "—";
        }
    }

    /* Map the endpoint status (enabled/pending/disabled) onto the existing visual classes
       (active/processing/inactive) so the colours stay unchanged. */
    private statusClass( status: string ): string {
        let variant = "unknown";

        switch( status ) {
            case "enabled":
                variant = "active";
                break;

            case "pending":
                variant = "processing";
                break;

            case "disabled":
                variant = "inactive";
                break;
        }

        return `c-group-users__status--${ variant }`;
    }

    /* Row click ("open") routes to the user's owning Console customer detail page. */
    private executeAction( id: string, rows: GroupUser[] ): void {
        if( id !== "open" || rows.length === 0 )
            return;

        const customerId = rows[ 0 ].CustomerId;

        if( !customerId )
            return;

        this.$router.push( `/customers/${ customerId }` );
    }

    private formatDate( value: string ): string {
        if( !value )
            return "—";

        const date = new Date( value );

        if( isNaN( date.getTime() ) )
            return "—";

        return date.toLocaleDateString( "fr-FR" );
    }

    /* Affiche le numéro au format national (E.164 "+33654477889" -> "06 54 47 78 89"). Numéro
       non parsable : renvoyé tel quel. Vide : tiret. */
    private formatPhone( value: string ): string {
        if( !value )
            return "—";

        const parsed = parsePhoneNumberFromString( value );

        return parsed !== undefined ? parsed.formatNational() : value;
    }
}
